import { validate as uuidValidate, v4 as uuidv4 } from 'uuid'
import { PostgrestError } from '@supabase/supabase-js'
import {
  dbUpsert,
  deleteAgamiFromTable,
  deleteAgamiMedia,
  getExperiencesForSpot,
  removeFile,
  singleDbInsert,
  singleDbUpsert,
  supabase,
  updateFile,
  uploadFile,
} from './supabase.helpers'
import {
  EXPERIENCE_CONTENT_TABLE,
  GEOXP_AUDIO_BUCKET,
  GEOXP_SPOTS_TABLE,
  EXPERIENCES_TABLE,
  USER_EXPERIENCES_TABLE,
  GEOXP_VIDEO_BUCKET,
} from '../constants/db.constants'
import { mimeTypeExtension } from './mime.helpers'
import { createManualCodeLibrarySuffix } from './agami.helpers'
import { isLegacyFilePath } from './validate.helpers'

export const uuidToPath = (uuid: string): string => {
  if (!uuidValidate(uuid)) {
    throw new Error('Invalid UUID format')
  }

  // Remove dashes from UUID and split into chunks
  const uuidWithoutDashes = uuid.replace(/-/g, '')
  const chunks = uuidWithoutDashes.match(/.{1,2}/g)
  // Join chunks with '/' and append original UUID
  return `${chunks!.slice(0, 3).join('/')}/${uuid}`
}

const uploadNewSpotMedia = async ({
  mediaFile,
  spotId,
  mediaType,
  locale,
}: {
  mediaFile: File
  spotId: string
  mediaType: number
  /** Additional-language media (08.2): the locale becomes a path suffix. */
  locale?: string
}) => {
  // upload as new file
  //
  // Path convention (08.1): the main language keeps xx/yy/zz/{uuid}.{ext}
  // UNCHANGED — it is the v1's path and the existing files'. An additional
  // language appends -{locale} AFTER uuidToPath, so the sharding stays and the
  // media of one agami live in the same directory.
  const fileExtension = mimeTypeExtension(mediaFile?.type)
  const uniqueFilePath = uuidToPath(spotId)
  const filename = `${uniqueFilePath}${locale ? `-${locale}` : ''}.${fileExtension}`

  const { data, error } = await uploadFile({
    filename,
    bucketId: mediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
    file: mediaFile,
  })
  return {
    uploadData: data,
    audioPath: (data as { path?: string })?.path,
    uploadError: error,
  }
}

const updateSpotMedia = async ({
  audioContentPath,
  mediaFile,
  spotId,
  mediaType,
  locale,
}: {
  audioContentPath: string
  mediaFile: File
  spotId: string
  mediaType: number
  locale?: string
}) => {
  const isLegacy = isLegacyFilePath(audioContentPath)

  // if legacy, upload new file and remove old one
  if (isLegacy) {
    const uploadRes = await uploadNewSpotMedia({
      mediaFile,
      spotId,
      mediaType,
      locale,
    })

    if (uploadRes.uploadError) {
      return uploadRes
    }

    // delete old file
    const { data: removeData, error: removeError } = await removeFile({
      filename: audioContentPath,
      bucketId: mediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
    })

    if (removeError) {
    }

    return {
      ...uploadRes,
      removeData,
      removeError,
    }
  }

  // if not legacy, upsert previous file
  const { data, error } = await updateFile({
    filename: audioContentPath,
    bucketId: mediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
    file: mediaFile,
  })

  return {
    uploadData: data,
    audioPath: (data as { path?: string })?.path,
    uploadError: error,
  }
}

/**
 * The stable "upload the media" step, shared by BOTH save orchestrators (06.1):
 * upsertAgamiSpot (v1) and upsertAgamiSpotV2. Exported for the v2 — the pieces
 * are shared, the flows are not, so that epics 07/08 can change the v2's data
 * shape without touching the form every venue uses today.
 */
export const uploadSpotMedia = async ({
  isUpdate,
  audioContentPath,
  mediaFile,
  spotId,
  mediaType,
  locale,
}: {
  isUpdate: boolean
  audioContentPath?: string
  mediaFile?: File | null
  spotId: string
  mediaType: number
  /** Absent → path unchanged (the v1's path); present → -{locale} suffix (08.2). */
  locale?: string
}): Promise<{
  audioPath?: string
  uploadData?: { path: string; fullPath: string; id: string } | null
  uploadError?: { message: string; statusCode?: string } | null
  removeData?: Array<{
    name: string
    id: string | null
    updated_at: string | null
    created_at: string | null
    last_accessed_at: string | null
    metadata: Record<string, unknown> | null
  }> | null
  removeError?: { message: string; statusCode?: string } | null
}> => {
  if (!mediaFile) return {}

  if (isUpdate) {
    const updateRes = await updateSpotMedia({
      audioContentPath: audioContentPath!,
      mediaFile,
      spotId,
      mediaType,
      locale,
    })
    return updateRes
  }

  // upload as new file
  const res = await uploadNewSpotMedia({
    mediaFile,
    spotId,
    mediaType,
    locale,
  })
  return res
}

const getManualCodeEntry = ({ type, manual_code }: { type?: number; manual_code?: string }) => {
  if (type === undefined || manual_code === undefined) {
    return
  }

  if (type === 1) {
    return { manual_code: null }
  }

  if (type === 2) {
    return { manual_code }
  }
}

export const upsertAgamiExperiences = async ({
  newExperiences,
  spot_id,
  type,
  manual_code,
  userDefaultExpId,
}: {
  newExperiences: string[]
  spot_id: string
  type?: number
  manual_code?: string
  userDefaultExpId?: string
}) => {
  // get all previous relations, also the canceled ones
  const { data: prevExpRelations, error: prevExpRelationsError } = await getExperiencesForSpot({ spot_id })

  if (prevExpRelationsError) {
    return { error: prevExpRelationsError }
  }

  const manualCodeEntry = getManualCodeEntry({ type, manual_code })

  // experiences previously active but not in the new array are those that now we want to cancel
  const toCancel =
    (prevExpRelations as Array<{ is_canceled: boolean; experience_id: string; id: string }>)
      ?.filter(
        (expRel: { is_canceled: boolean; experience_id: string }) =>
          !expRel.is_canceled && !newExperiences.includes(expRel.experience_id)
      )
      ?.map((expRel: { id: string; experience_id: string }) => ({
        ...(manualCodeEntry && { manual_code: manualCodeEntry.manual_code }),
        id: expRel.id,
        experience_id: expRel.experience_id,
        spot_id,
        is_canceled: true,
      })) ?? []

  // experiences relations in the new new array should be added to upsert
  const toAddOrUpdate = newExperiences?.map((expId: string) => {
    // check if already present a row
    const relationId = (
      prevExpRelations as Array<{ experience_id: string; spot_id: string; id: string }>
    )?.find(
      (expRel: { experience_id: string; spot_id: string; id: string }) =>
        expRel.experience_id === expId && expRel.spot_id === spot_id
    )

    // manage manual spots in user library:
    // append §lib_<uuid> to insert without duplicates conflicts
    if (expId === userDefaultExpId && manualCodeEntry && manualCodeEntry.manual_code) {
      const manualCodeWithSuffix = createManualCodeLibrarySuffix(manualCodeEntry.manual_code)
      return {
        ...(relationId && { id: relationId.id }),
        manual_code: manualCodeWithSuffix,
        spot_id,
        experience_id: expId,
        is_canceled: false,
      }
    }

    return {
      ...(relationId && { id: relationId.id }),
      ...(manualCodeEntry && { manual_code: manualCodeEntry.manual_code }),
      spot_id,
      experience_id: expId,
      is_canceled: false,
    }
  })

  const upsertData = [...toCancel, ...toAddOrUpdate]

  // upsert to experience_content_spots table
  const { data, error } = await dbUpsert({
    upsertData,
    table: EXPERIENCE_CONTENT_TABLE,
    upsertOptions: {
      onConflict: 'id',
      defaultToNull: false,
    },
  })

  return { data, error }
}

export const upsertAgamiSpot = async ({
  userId,
  experiences,
  spotName,
  description,
  latitude,
  longitude,
  radius,
  deadband,
  mediaFile,
  mediaFilename,
  type,
  manual_code,
  onSuccess,
  onError,
  spotId,
  audioContentPath,
  userDefaultExpId,
  is_draft,
  content_media_type,
  landing_page_code_id,
}: {
  userId: string
  experiences: string[]
  spotName: string
  description: string
  latitude?: number
  longitude?: number
  radius?: number
  deadband?: number
  mediaFile?: File | null
  mediaFilename?: string
  type?: number
  manual_code?: string
  onSuccess: (data?: {
    uploadData?: { path: string; fullPath: string; id: string } | null
    insertSpotData?: Record<string, unknown> | Record<string, unknown>[] | null
    upsertExpContentData?: Record<string, unknown> | Record<string, unknown>[] | null
  }) => void
  onError: (error: PostgrestError | Error | { message: string; statusCode?: string }) => void
  spotId?: string
  audioContentPath?: string
  userDefaultExpId?: string
  is_draft: boolean
  content_media_type: number
  landing_page_code_id?: string | null
}) => {
  if (!onSuccess || !onError) {
    return
  }

  // check if it's a new spot or already existing
  const isUpdate = !!spotId

  const newId = isUpdate ? spotId : uuidv4()

  // upload audio file to object storage bucket
  const { audioPath, uploadData, uploadError } = await uploadSpotMedia({
    isUpdate,
    audioContentPath,
    mediaFile,
    spotId: newId!,
    mediaType: content_media_type,
  })

  if (uploadError) {
    onError(uploadError)
    return
  }

  // upsert DB record
  const { data: insertSpotData, error: insertSpotError } = await singleDbUpsert({
    table: GEOXP_SPOTS_TABLE,
    upsertData: {
      description,
      type,
      user_id: userId,
      name: spotName,
      audio_filename: mediaFilename,
      id: newId,
      audio_content_path: audioPath,
      latitude: latitude ?? null,
      longitude: longitude ?? null,
      radius: radius ?? null,
      deadband: deadband ?? null,
      is_draft,
      content_media_type,
      // Only manual spots (type=2) can be associated with a landing page; for
      // geo spots we always force NULL so editing geo doesn't carry over a
      // stale association from a previous manual-type session.
      landing_page_code_id: type === 2 ? (landing_page_code_id ?? null) : null,
    },
  })

  if (insertSpotError) {
    // revert uploaded file
    await deleteAgamiMedia({
      audioPath: audioPath!,
      contentMediaType: content_media_type,
    })
    // call onError
    onError(insertSpotError as Error)
    return
  }

  const id = insertSpotData?.id

  // upsert to experience_content_spots table
  const { data: upsertExpContentData, error: upsertExpContentError } = await upsertAgamiExperiences({
    manual_code,
    type,
    spot_id: id!,
    newExperiences: experiences,
    userDefaultExpId,
  })

  if (upsertExpContentError) {
    // revert upserted spot
    await deleteAgamiFromTable(id)
    // revert uploaded file
    await deleteAgamiMedia({
      audioPath: audioPath!,
      contentMediaType: content_media_type,
    })
    // call onError
    onError(upsertExpContentError as Error)
    return
  }

  if (isUpdate) onSuccess({ insertSpotData, upsertExpContentData })
  else onSuccess({ uploadData, insertSpotData, upsertExpContentData })
}

export const createExperience = async ({ insertData }: { insertData: Record<string, unknown> }) => {
  if (!insertData) {
    return { error: 'No insertData provided' }
  }

  // insert in exp tables
  const { data: newExpData, error: newExpError } = await singleDbInsert({
    table: EXPERIENCES_TABLE,
    insertData,
  })

  if (newExpError) {
    return { error: newExpError }
  }

  const { id, owner_id } = newExpData as { id: string; owner_id: string }

  // assign to current user as owner
  const { data, error } = await singleDbInsert({
    table: USER_EXPERIENCES_TABLE,
    insertData: {
      experience_id: id,
      user_id: owner_id,
      experience_role: 1, // owner
    },
  })

  return {
    data: {
      ...(data as Record<string, unknown>),
      ...(newExpData as Record<string, unknown>),
    },
    error,
  }
}

export const cleanFileName = ({ file }: { file?: File | null }): string | undefined => {
  if (!file) return
  return file?.name?.replace(/[,:;'!?"`§·]/g, '')?.replace(/ /g, '_')
}

export const updateExperienceDetails = async ({ expNewData }: { expNewData: Record<string, unknown> }) => {
  const { id, ...dataToUpdate } = expNewData
  const { error } = await supabase.from(EXPERIENCES_TABLE).update(dataToUpdate).eq('id', id)
  return { error }
}
